import styled, { css } from 'styled-components';

import { ThemeProp } from '../../theme/Themes';
import Avatar from '../Avatar';
import Text from '../Text';

const MAX_MESSAGE_WIDTH = '21.25rem';
const MESSAGE_HORZ_PADDING = '0.75rem';

interface ContainerProps {
  $isLoggedUserSide?: boolean;
  $isSending?: boolean;
}
export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: ${(props: ContainerProps) => (props.$isLoggedUserSide ? 'row-reverse' : 'row')};
  gap: 0.5rem;
  opacity: ${(props: ContainerProps) => (props.$isSending ? '0.2' : '1')};
`;

interface AvatarProps {
  $isConnected?: boolean;
  $bordered?: boolean;
}

export const UserAvatarContainer = styled.div<AvatarProps>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: ${(props: AvatarProps & ThemeProp) =>
    props.$bordered
      ? `2px solid ${props.$isConnected ? props.theme.colors.system.success : props.theme.colors.neutrals.c300}`
      : 'none'};
`;

export const UserImage = styled(Avatar).attrs(() => ({
  width: '1.64rem',
  height: '1.64rem',
}))`
  .nextui-avatar-text {
    font-size: 1.12rem;
  }
`;

export const OrganizatitonAvatar = styled(Avatar).attrs(() => ({
  width: '0.75rem',
  height: '0.75rem',
}))`
  position: absolute;
  right: -0.125rem;
  bottom: -0.125rem;

  .nextui-avatar-text {
    font-size: 0.5rem;
  }
`;

interface MessageDataContainerProps {
  $isLoggedUserSide?: boolean;
  $minWidth?: string;
  $isShimmer?: boolean;
  $isError?: boolean;
}
export const MessageDataContainer = styled.div<MessageDataContainerProps>`
  display: flex;
  flex-direction: column;
  max-width: ${MAX_MESSAGE_WIDTH};
  min-width: ${(props: MessageDataContainerProps) => (props.$isError ? '15rem' : '0')};
  padding: 0.625rem ${MESSAGE_HORZ_PADDING} 0.5rem ${MESSAGE_HORZ_PADDING};
  border-radius: 0.5rem;
  border-top-left-radius: ${(props: MessageDataContainerProps) => (props.$isLoggedUserSide ? '0.5rem' : '0')};
  border-top-right-radius: ${(props: MessageDataContainerProps) => (props.$isLoggedUserSide ? '0' : '0.5rem')};
  background: ${(props: ThemeProp & MessageDataContainerProps) =>
    props.$isError
      ? props.theme.colors.layout.surface.default
      : props.$isLoggedUserSide && !props.$isShimmer
      ? props.theme.colors.main.c050
      : props.theme.colors.neutrals.c050};
  ${(props: MessageDataContainerProps) => props.$minWidth && `min-width: ${props.$minWidth};`}
  word-break: break-word;
  ${(props: MessageDataContainerProps) =>
    props.$isError &&
    css`
      border: 1px solid ${({ theme }: ThemeProp) => theme.colors.layout.divider};
    `};
`;

interface MessageUser {
  $isSender?: boolean;
}

export const MessageUser = styled(Text)<MessageUser>`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xs};
  line-height: ${(props: ThemeProp) => props.theme.fontSize.m};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.bold};
  color: ${(props: MessageUser & ThemeProp) =>
    props.$isSender ? props.theme.colors.main.c800 : props.theme.colors.neutrals.c800};
`;

export const MessageText = styled(Text)`
  color: ${(props: ThemeProp) => props.theme.colors.content.default};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.fontSize};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.body2.lineHeight};
  white-space: pre-line;
  margin-top: 0.25rem;
  word-break: break-word;
  overflow-wrap: break-word;
`;

export const MessageImagePreviewContainer = styled.div`
  display: flex;
  position: relative;
  width: 3.375rem;
  height: 3.375rem;
  border-radius: 0.25rem;
  cursor: pointer;
  transition: all ease-out 0.3s, outline 0s;

  svg {
    display: none;
    position: absolute;
  }

  &.is-expanded {
    width: calc(100% - 2 * ${MESSAGE_HORZ_PADDING});
    height: 100%;

    svg {
      display: flex;
      top: 0.625rem;
      right: 0.625rem;

      background: ${(props: ThemeProp) => props.theme.colors.black};
      border-radius: 0.25rem;

      padding: 0.094rem;
    }
  }

  &.is-not-expanded {
    :hover {
      outline: 1px solid ${(props: ThemeProp) => props.theme.colors.primary.c500};

      svg {
        display: flex;
        margin-top: auto;
        margin-bottom: auto;
        margin-left: auto;
        margin-right: auto;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
      }
    }
  }
`;

export const MessageImagePreview = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 0.25rem;
  object-fit: cover;

  &.is-not-expanded:hover {
    opacity: 0.1;
  }
`;

export const MessageTimestamp = styled(Text)`
  font-size: ${(props: ThemeProp) => props.theme.fontSize.xxs};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.medium};
  color: ${(props: ThemeProp) => props.theme.colors.content.subdued.high};
  letter-spacing: 0.03125rem;
  opacity: 0.5;
  align-self: flex-end;
`;

export const MessageWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

export const ErrorContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  cursor: pointer;
  gap: 0.25rem;
  overflow: hidden;
  margin-top: 0.25rem;
`;

export const ErrorText = styled.span`
  color: ${(props: ThemeProp) => props.theme.colors.system.error};
  font-size: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.fontSize};
  font-weight: ${(props: ThemeProp) => props.theme.fontWeight.regular};
  line-height: ${(props: ThemeProp) => props.theme.fontVariants.desktop.caption1.lineHeight};
  margin-top: 0.05rem;
`;

export const MessageDataWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

export const MessagePDFPreviewContainer = styled(MessageImagePreviewContainer)`
  background: ${(props: ThemeProp) => props.theme.colors.main.c100};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem;
  gap: 0.5rem;
  cursor: pointer;

  svg {
    width: 3.2rem;
    height: 3.2rem;
    display: block;
  }

  span {
    color: ${(props: ThemeProp) => props.theme.colors.main.c800};
    font-weight: 500;
  }
`;

export const MessageFilePreviewContainer = styled(MessagePDFPreviewContainer)`
  color: ${(props: ThemeProp) => props.theme.colors.main.c600};
  svg {
    width: 2rem;
    height: 2rem;
    display: block;
  }
`;
